import { useState, useEffect, useRef } from "react";

const FEATURES = [
  {
    icon: "⏱",
    title: "Daily Recordings",
    desc: "Real-time check-in and check-out tracking with precise timestamps for every employee.",
  },
  {
    icon: "💳",
    title: "Financial",
    desc: "Manage salaries, deductions, bonuses and payment records all in one place.",
  },
  {
    icon: "🗓",
    title: "Holidays & Leaves",
    desc: "Plan, request, and approve official leaves and vacations effortlessly.",
  },
  {
    icon: "📊",
    title: "Reports",
    desc: "Generate detailed attendance and financial insights with exportable reports.",
  },
  {
    icon: "⚙️",
    title: "App Settings",
    desc: "Customize roles, permissions, and preferences to fit your organization.",
  },
  {
    icon: "⭐",
    title: "Premium Features",
    desc: "Unlock advanced workforce analytics and automation tools.",
  },
];

const STATS = [
  { value: "10K+", label: "Active Users" },
  { value: "98%", label: "Uptime" },
  { value: "50+", label: "Companies" },
  { value: "4.8★", label: "App Rating" },
];

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [visible, setVisible] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible((v) => ({ ...v, [e.target.dataset.id]: true }));
        });
      },
      { threshold: 0.15 }
    );
    Object.values(refs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const reg = (id) => (el) => {
    refs.current[id] = el;
    if (el) el.dataset.id = id;
  };

  const fadeIn = (id, delay = 0) => ({
    opacity: visible[id] ? 1 : 0,
    transform: visible[id] ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #0d0f14;
          --bg2: #12151c;
          --card: #181c26;
          --border: rgba(255,255,255,0.07);
          --blue: #3b82f6;
          --blue-bright: #60a5fa;
          --blue-glow: rgba(59,130,246,0.18);
          --text: #f0f4ff;
          --muted: #7a849a;
          --accent: #0ea5e9;
        }

        html { scroll-behavior: smooth; }

        body {
          background: var(--bg);
          color: var(--text);
          font-family: 'DM Sans', sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }

        h1,h2,h3,h4,h5 { font-family: 'Syne', sans-serif; }

        /* NAV */
        .nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 6vw;
          height: 68px;
          transition: background 0.4s, backdrop-filter 0.4s, border-bottom 0.4s;
        }
        .nav.scrolled {
          background: rgba(13,15,20,0.85);
          backdrop-filter: blur(18px);
          border-bottom: 1px solid var(--border);
        }
        .nav-logo {
          font-family: 'Syne', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--text);
          text-decoration: none;
          display: flex; align-items: center; gap: 10px;
        }
        .nav-logo span {
          display: inline-block;
          width: 34px; height: 34px;
          background: var(--blue);
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1rem;
        }
        .nav-links { display: flex; align-items: center; gap: 36px; list-style: none; }
        .nav-links a {
          color: var(--muted); font-size: 0.92rem; text-decoration: none;
          transition: color 0.2s; font-weight: 500;
        }
        .nav-links a:hover { color: var(--text); }
        .nav-cta {
          background: var(--blue); color: #fff; border: none; border-radius: 10px;
          padding: 10px 22px; font-size: 0.9rem; font-weight: 600;
          cursor: pointer; font-family: 'Syne', sans-serif;
          transition: background 0.2s, transform 0.15s;
          text-decoration: none;
        }
        .nav-cta:hover { background: var(--blue-bright); transform: translateY(-1px); }

        .hamburger {
          display: none; flex-direction: column; gap: 5px;
          cursor: pointer; background: none; border: none; padding: 4px;
        }
        .hamburger span {
          display: block; width: 24px; height: 2px;
          background: var(--text); border-radius: 2px; transition: 0.3s;
        }

        .mobile-menu {
          display: none; position: fixed; top: 68px; left: 0; right: 0;
          background: rgba(13,15,20,0.97); backdrop-filter: blur(18px);
          padding: 24px 6vw; border-bottom: 1px solid var(--border);
          flex-direction: column; gap: 20px; z-index: 99;
        }
        .mobile-menu.open { display: flex; }
        .mobile-menu a {
          color: var(--muted); font-size: 1rem; text-decoration: none;
          font-weight: 500; transition: color 0.2s;
        }
        .mobile-menu a:hover { color: var(--text); }

        /* HERO */
        .hero {
          min-height: 100vh;
          display: flex; align-items: center;
          padding: 100px 6vw 60px;
          position: relative; overflow: hidden;
        }
        .hero-bg {
          position: absolute; inset: 0; z-index: 0;
          background: radial-gradient(ellipse 80% 60% at 60% 40%, rgba(59,130,246,0.12) 0%, transparent 70%),
                      radial-gradient(ellipse 50% 50% at 20% 80%, rgba(14,165,233,0.07) 0%, transparent 60%);
        }
        .hero-grid {
          position: absolute; inset: 0; z-index: 0;
          background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 0%, transparent 100%);
        }
        .hero-inner {
          position: relative; z-index: 1;
          max-width: 1200px; margin: 0 auto; width: 100%;
          display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center;
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--blue-glow); border: 1px solid rgba(59,130,246,0.3);
          border-radius: 100px; padding: 6px 14px;
          font-size: 0.82rem; color: var(--blue-bright); font-weight: 500;
          margin-bottom: 20px;
        }
        .hero-badge::before { content: '●'; font-size: 0.5rem; }
        .hero h1 {
          font-size: clamp(2.4rem, 5vw, 4rem);
          font-weight: 800; line-height: 1.1;
          letter-spacing: -0.03em;
          margin-bottom: 20px;
        }
        .hero h1 .highlight {
          background: linear-gradient(135deg, var(--blue), var(--accent));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero p {
          color: var(--muted); font-size: 1.05rem; max-width: 460px;
          margin-bottom: 36px; line-height: 1.75;
        }
        .hero-actions { display: flex; gap: 14px; flex-wrap: wrap; }
        .btn-primary {
          background: var(--blue); color: #fff;
          border: none; border-radius: 12px;
          padding: 14px 28px; font-size: 0.95rem; font-weight: 600;
          cursor: pointer; font-family: 'Syne', sans-serif;
          display: inline-flex; align-items: center; gap: 8px;
          text-decoration: none;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 0 28px rgba(59,130,246,0.35);
        }
        .btn-primary:hover {
          background: var(--blue-bright); transform: translateY(-2px);
          box-shadow: 0 0 40px rgba(59,130,246,0.5);
        }
        .btn-ghost {
          background: transparent; color: var(--text);
          border: 1px solid var(--border); border-radius: 12px;
          padding: 14px 28px; font-size: 0.95rem; font-weight: 500;
          cursor: pointer; font-family: 'DM Sans', sans-serif;
          text-decoration: none; transition: border-color 0.2s, background 0.2s;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .btn-ghost:hover { border-color: rgba(59,130,246,0.5); background: var(--blue-glow); }

        /* Phone mockup */
        .hero-visual { display: flex; justify-content: center; align-items: center; position: relative; }
        .phone-wrap {
          position: relative;
          animation: float 5s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-16px); }
        }
        .phone {
          width: 220px; height: 440px;
          background: var(--card);
          border-radius: 36px;
          border: 1.5px solid rgba(255,255,255,0.1);
          position: relative; overflow: hidden;
          box-shadow: 0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset;
        }
        .phone-screen {
          position: absolute; inset: 0;
          background: linear-gradient(160deg, #0f1623 0%, #0d1420 100%);
          display: flex; flex-direction: column;
          padding: 40px 18px 18px;
          gap: 12px;
        }
        .phone-notch {
          position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
          width: 70px; height: 6px; background: #1e2535; border-radius: 100px;
        }
        .phone-header { font-family: 'Syne', sans-serif; font-size: 0.75rem; font-weight: 700; color: var(--text); }
        .phone-time { font-size: 1.6rem; font-weight: 800; font-family: 'Syne', sans-serif; color: var(--text); }
        .phone-sub { font-size: 0.65rem; color: var(--muted); margin-top: -8px; }
        .phone-card {
          background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.2);
          border-radius: 14px; padding: 10px 12px;
          display: flex; align-items: center; gap: 10px;
        }
        .phone-card-icon {
          width: 30px; height: 30px; border-radius: 8px;
          background: var(--blue); display: flex; align-items: center; justify-content: center;
          font-size: 0.8rem; flex-shrink: 0;
        }
        .phone-card-label { font-size: 0.6rem; color: var(--muted); }
        .phone-card-val { font-size: 0.78rem; font-weight: 600; color: var(--text); font-family: 'Syne',sans-serif; }
        .phone-row { display: flex; gap: 8px; }
        .phone-mini {
          flex: 1; background: var(--bg2); border: 1px solid var(--border);
          border-radius: 12px; padding: 8px; text-align: center;
        }
        .phone-mini-icon { font-size: 1rem; }
        .phone-mini-label { font-size: 0.55rem; color: var(--muted); margin-top: 2px; }
        .phone-glow {
          position: absolute; width: 200px; height: 200px;
          border-radius: 50%; background: rgba(59,130,246,0.15);
          filter: blur(40px); top: 50%; left: 50%; transform: translate(-50%, -50%);
          pointer-events: none; z-index: -1;
        }

        /* STATS */
        .stats-section {
          padding: 20px 6vw 60px;
          max-width: 1200px; margin: 0 auto;
        }
        .stats-grid {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 1px; background: var(--border);
          border: 1px solid var(--border); border-radius: 20px; overflow: hidden;
        }
        .stat-item {
          background: var(--card);
          padding: 28px 24px; text-align: center;
        }
        .stat-value {
          font-family: 'Syne', sans-serif; font-size: 2rem; font-weight: 800;
          background: linear-gradient(135deg, var(--text), var(--blue-bright));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-label { color: var(--muted); font-size: 0.85rem; margin-top: 4px; }

        /* FEATURES */
        .features-section {
          padding: 80px 6vw;
          max-width: 1200px; margin: 0 auto;
        }
        .section-tag {
          display: inline-block;
          font-size: 0.78rem; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--blue-bright);
          margin-bottom: 14px;
        }
        .section-title {
          font-size: clamp(1.8rem, 3.5vw, 2.8rem);
          font-weight: 800; letter-spacing: -0.02em;
          margin-bottom: 14px;
        }
        .section-sub {
          color: var(--muted); font-size: 1rem; max-width: 500px; line-height: 1.75;
        }
        .features-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 20px; margin-top: 56px;
        }
        .feature-card {
          background: var(--card); border: 1px solid var(--border);
          border-radius: 20px; padding: 28px;
          transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s;
          cursor: default;
        }
        .feature-card:hover {
          border-color: rgba(59,130,246,0.4);
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 30px var(--blue-glow);
        }
        .feature-icon {
          width: 48px; height: 48px; border-radius: 14px;
          background: var(--blue-glow); border: 1px solid rgba(59,130,246,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.3rem; margin-bottom: 18px;
        }
        .feature-title {
          font-family: 'Syne', sans-serif; font-size: 1rem; font-weight: 700;
          margin-bottom: 8px;
        }
        .feature-desc { color: var(--muted); font-size: 0.88rem; line-height: 1.65; }

        /* HOW IT WORKS */
        .how-section {
          padding: 80px 6vw;
          background: linear-gradient(180deg, transparent, var(--bg2) 20%, var(--bg2) 80%, transparent);
        }
        .how-inner { max-width: 1200px; margin: 0 auto; }
        .how-steps {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 40px; margin-top: 56px; position: relative;
        }
        .how-steps::before {
          content: ''; position: absolute;
          top: 28px; left: calc(16.6% + 14px); right: calc(16.6% + 14px);
          height: 1px; background: linear-gradient(90deg, transparent, var(--blue), transparent);
          z-index: 0;
        }
        .step {
          text-align: center; position: relative; z-index: 1;
        }
        .step-num {
          width: 56px; height: 56px; border-radius: 50%;
          background: var(--blue); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-family: 'Syne', sans-serif; font-size: 1.1rem; font-weight: 800;
          margin: 0 auto 20px;
          box-shadow: 0 0 24px rgba(59,130,246,0.5);
        }
        .step-title { font-family: 'Syne',sans-serif; font-weight: 700; margin-bottom: 10px; }
        .step-desc { color: var(--muted); font-size: 0.88rem; line-height: 1.65; }

        /* CTA */
        .cta-section {
          padding: 80px 6vw;
          max-width: 1200px; margin: 0 auto;
        }
        .cta-box {
          background: var(--card); border: 1px solid var(--border);
          border-radius: 28px; padding: 60px 48px;
          text-align: center; position: relative; overflow: hidden;
        }
        .cta-box::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse 70% 70% at 50% 50%, rgba(59,130,246,0.08) 0%, transparent 70%);
        }
        .cta-box h2 {
          font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800;
          letter-spacing: -0.02em; margin-bottom: 14px; position: relative;
        }
        .cta-box p { color: var(--muted); font-size: 1rem; margin-bottom: 36px; position: relative; }
        .cta-actions { display: flex; justify-content: center; gap: 14px; flex-wrap: wrap; position: relative; }
        .store-btn {
          display: inline-flex; align-items: center; gap: 10px;
          background: var(--bg); border: 1px solid var(--border);
          border-radius: 14px; padding: 14px 24px; text-decoration: none;
          transition: border-color 0.2s, background 0.2s, transform 0.15s;
          min-width: 160px;
        }
        .store-btn:hover {
          border-color: rgba(59,130,246,0.5); background: var(--blue-glow);
          transform: translateY(-2px);
        }
        .store-icon { font-size: 1.6rem; }
        .store-label { font-size: 0.65rem; color: var(--muted); }
        .store-name { font-family: 'Syne',sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); }

        /* FOOTER */
        footer {
          border-top: 1px solid var(--border);
          padding: 40px 6vw;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 20px;
        }
        .footer-logo {
          font-family: 'Syne', sans-serif; font-weight: 800; font-size: 1.2rem;
          color: var(--text); text-decoration: none;
        }
        .footer-links { display: flex; gap: 28px; list-style: none; }
        .footer-links a { color: var(--muted); font-size: 0.88rem; text-decoration: none; transition: color 0.2s; }
        .footer-links a:hover { color: var(--text); }
        .footer-copy { color: var(--muted); font-size: 0.82rem; }

        /* ANIMATIONS */
        @keyframes heroIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-content > * { animation: heroIn 0.8s ease both; }
        .hero-content > *:nth-child(1) { animation-delay: 0.1s; }
        .hero-content > *:nth-child(2) { animation-delay: 0.25s; }
        .hero-content > *:nth-child(3) { animation-delay: 0.4s; }
        .hero-content > *:nth-child(4) { animation-delay: 0.55s; }
        .hero-visual { animation: heroIn 0.9s ease 0.3s both; }

        @media (max-width: 900px) {
          .hero-inner { grid-template-columns: 1fr; gap: 40px; text-align: center; }
          .hero p { max-width: 100%; }
          .hero-actions { justify-content: center; }
          .hero-visual { display: none; }
          .features-grid { grid-template-columns: repeat(2, 1fr); }
          .how-steps { grid-template-columns: 1fr; }
          .how-steps::before { display: none; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .nav-links { display: none; }
          .nav-cta-desktop { display: none; }
          .hamburger { display: flex; }
        }
        @media (max-width: 580px) {
          .features-grid { grid-template-columns: 1fr; }
          .cta-box { padding: 40px 24px; }
        }
      `}</style>

      {/* NAV */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <a href="#" className="nav-logo">
           <img
            src="/assets/images/logo.png"
            alt="Dawami"
            style={{height:60,with:60}}
          />
           Dawami
        </a>
        <ul className="nav-links">
          <li><a href="#about">About</a></li>
          <li><a href="#features">Features</a></li>
          <li><a href="#how">How it works</a></li>
          <li><a href="/privacy-policy">Privacy</a></li>
        </ul>
        <a
          href="#download"
          className="nav-cta nav-cta-desktop"
         
        >
          Download App
        </a>
        <button className="hamburger" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
        <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
        <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
        <a href="/privacy-policy" onClick={() => setMenuOpen(false)}>Privacy</a>
        <a href="#download" >Download</a>
      </div>

      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-bg" />
        <div className="hero-grid" />
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-badge">Smart Attendance Management</div>
            <h1>
              Manage Your <span className="highlight">Workforce</span> With Confidence
            </h1>
            <p>
              Dawami helps employees and managers track attendance, salaries,
              holidays, and financial records — all in one powerful, easy-to-use app.
            </p>
            <div className="hero-actions">
              <a
                href="#download"
                className="btn-primary" 
              >
                ↓ Download Free
              </a>
              <a href="#features" className="btn-ghost">See Features →</a>
            </div>
          </div>
          <div className="hero-visual">
            <div className="phone-wrap">
              <div className="phone-glow" />
              <div className="phone">
                <div className="phone-screen">
                  <div className="phone-notch" />
                  <div className="phone-header">Good morning, Ahmed 👋</div>
                  <div className="phone-time">09:14 AM</div>
                  <div className="phone-sub">Tuesday, March 2026</div>
                  <div className="phone-card">
                    <div className="phone-card-icon">✅</div>
                    <div>
                      <div className="phone-card-label">Today's Status</div>
                      <div className="phone-card-val">Checked In — 09:02 AM</div>
                    </div>
                  </div>
                  <div className="phone-card">
                    <div className="phone-card-icon">💰</div>
                    <div>
                      <div className="phone-card-label">This Month</div>
                      <div className="phone-card-val">Salary: EGP 4,200</div>
                    </div>
                  </div>
                  <div className="phone-row">
                    <div className="phone-mini">
                      <div className="phone-mini-icon">📅</div>
                      <div className="phone-mini-label">22 Days</div>
                    </div>
                    <div className="phone-mini">
                      <div className="phone-mini-icon">🏖</div>
                      <div className="phone-mini-label">3 Leaves</div>
                    </div>
                    <div className="phone-mini">
                      <div className="phone-mini-icon">📊</div>
                      <div className="phone-mini-label">Reports</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="stats-section" id="about">
        <div className="stats-grid" ref={reg("stats")}>
          {STATS.map((s, i) => (
            <div className="stat-item" key={i} style={fadeIn("stats", i * 0.1)}>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <section className="features-section" id="features">
        <div ref={reg("feat-head")} style={fadeIn("feat-head")}>
          <span className="section-tag">Everything You Need</span>
          <h2 className="section-title">Packed with powerful features</h2>
          <p className="section-sub">
            From daily attendance to monthly financials — Dawami covers every aspect
            of workforce management in one app.
          </p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div
              className="feature-card"
              key={i}
              ref={reg(`feat-${i}`)}
              style={fadeIn(`feat-${i}`, (i % 3) * 0.1)}
            >
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-title">{f.title}</div>
              <div className="feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section" id="how">
        <div className="how-inner">
          <div ref={reg("how-head")} style={fadeIn("how-head")}>
            <span className="section-tag">Simple Setup</span>
            <h2 className="section-title">Up and running in minutes</h2>
          </div>
          <div className="how-steps">
            {[
              { n: "1", t: "Download the App", d: "Available on Android and iOS. Free to install and get started immediately." },
              { n: "2", t: "Set Up Your Team", d: "Add your employees, set work hours, configure salaries and leave policies." },
              { n: "3", t: "Track & Manage", d: "Monitor attendance in real time, generate reports, and manage payroll effortlessly." },
            ].map((s, i) => (
              <div className="step" key={i} ref={reg(`step-${i}`)} style={fadeIn(`step-${i}`, i * 0.15)}>
                <div className="step-num">{s.n}</div>
                <div className="step-title">{s.t}</div>
                <div className="step-desc">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="download">
        <div className="cta-box" ref={reg("cta")} style={fadeIn("cta")}>
          <h2>Ready to streamline your attendance?</h2>
          <p>Join thousands of teams already using Dawami to manage their workforce smarter.</p>
          <div className="cta-actions">
            <a
              href="https://play.google.com/store/apps/details?id=com.applet.dawami"
              className="store-btn" target="_blank" rel="noreferrer"
            >
              <span className="store-icon">🤖</span>
              <div>
                <div className="store-label">Get it on</div>
                <div className="store-name">Google Play</div>
              </div>
            </a>
            <a
              href="https://apps.apple.com/us/app/dawamix/id6741182545"
              className="store-btn" target="_blank" rel="noreferrer"
            >
              <span className="store-icon">🍎</span>
              <div>
                <div className="store-label">Download on the</div>
                <div className="store-name">App Store</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <a href="#" className="footer-logo">⏱ Dawami</a>
        <ul className="footer-links">
          <li><a href="#features">Features</a></li>
          <li><a href="/privacy-policy">Privacy Policy</a></li>
          <li><a href="https://www.facebook.com/nursingprometric994" target="_blank" rel="noreferrer">Contact</a></li>
        </ul>
        <span className="footer-copy">© 2026 Dawami. All rights reserved.</span>
      </footer>
    </>
  );
}